import React, { useState } from "react";
import {
  User,
  MapPin,
  CreditCard,
  Settings,
  History,
  LogOut,
  Zap,
  ArrowLeftRight,
} from "lucide-react";

import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Card, CardContent } from "../ui/card";
import { Link, useNavigate } from "react-router-dom";
import { useSignOut } from "@/hooks";
import ls from "@/utils/SecureLS";
import { setDistributorInfo, setStylistInfo, setUser } from "@/store/slices/authSlice";
import { updateUserInfo } from "@/graphql/mutations";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { listSellerInfos, listStylistInfos } from "@/graphql/queries";
import { UserInfo } from "@/types/AppTypes";
import { getUserFromLS } from "@/utils/helper/getUserFromLS";
import { toast } from "sonner";

const menuItems = [
  {
    title: "Profile",
    icon: User,
    id: "profile",
  },
  {
    title: "Addresses",
    icon: MapPin,
    id: "addresses",
  },
  {
    title: "Payment Methods",
    icon: CreditCard,
    id: "payment-methods",
  },
  {
    title: "Preferences",
    icon: Settings,
    id: "preferences",
  },
  {
    title: "Quick Reorder",
    icon: Zap,
    id: "quick-reorder",
    // link: "/quick-reorder",
  },
  {
    title: "Order History",
    icon: History,
    id: "order-history",
    // link: "/order-history",
  },
];
type AppSidebarProps = {
  activeTab: string;
  onTabChange: (tab: string) => void;
};


const UserAccountSidebar = ({ activeTab, onTabChange }: AppSidebarProps) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const user = getUserFromLS();
  const { handleSignOut } = useSignOut();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const handleSwitchRole = async () => {
    const result = await executeGraphQLMutation({
      query: listSellerInfos,
      variables: {
        filter: {
          user_id: {
            eq: user?.user_id,
          },
        },
      },
    });

    if (result?.items?.length > 0 && !result?.items?.[0]?.is_verified) {
      toast.error("You are not verified as a seller yet");
      return;
    }

  
    const userinfo = await executeGraphQLMutation({
      query: updateUserInfo,
      variables: {
        input: {
          id: user?.id,
          current_role: "seller",
          updated_at: new Date().toISOString(),
        },
      },

      showToast: true,
      setLoading,
    });

    const distributorInfo = await executeGraphQLMutation({
      query: listSellerInfos,
      variables: {
        filter: {
          user_id: {
            eq: user?.user_id,
          },
        },
      },

      setLoading,
    });

    if (distributorInfo?.items?.length > 0) {
      dispatch(setDistributorInfo(distributorInfo?.items[0]));
    }

    if (userinfo?.email) {
      ls.set("user", JSON.stringify(userinfo));
      dispatch(setUser(userinfo));
      navigate("/seller");
    }
  };

  return (
    <Card className="w-full shadow-sm py-2">
      <CardContent className="p-0">
        <div className="p-3">
          {menuItems.map((item: any) =>
            item?.link ? (
              <Link to={item?.link} key={item.id}>
                <Button
                  variant="ghost"
                  className="w-full justify-start h-10 mb-1 text-sm font-medium hover:bg-gray-100 text-gray-700"
                >
                  <item.icon className="mr-3 h-4 w-4" />
                  <span>{item.title}</span>
                </Button>
              </Link>
            ) : (
              <Button
                key={item.id}
                variant={activeTab === item.id ? "default" : "ghost"}
                className={`w-full justify-start h-10 mb-1 text-sm font-medium ${
                  activeTab === item.id
                    ? "bg-black text-white hover:bg-gray-800 shadow-sm"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
                onClick={() => onTabChange(item.id)}
              >
                <item.icon className="mr-3 h-4 w-4" />
                <span>{item.title}</span>
              </Button>
            )
          )}

          {isAuthenticated && user?.has_roles?.includes("seller") ? (
            <Button
              onClick={handleSwitchRole}
              className="group p-3 hover:bg-gray-100 hover:text-black rounded-md  bg-transparent text-gray-700 my-1 w-full flex items-center justify-start"
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              {loading ? <span className="ml-4">Switching...</span> : <>Switch to Distributor</>}
            </Button>
          ) : (
            <></>
          )}

          <Separator className="my-2" />

          <Button
            variant="ghost"
            className="w-full justify-start h-10 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleSignOut}
          >
            <LogOut className="mr-3 h-4 w-4" />
            <span>Logout</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserAccountSidebar;
